/**
 * Predictive Search Recent Searches
 * Håndterer recent searches funktionalitet (localStorage, visning, klik)
 * Kopieret og tilpasset fra Helloretail mobile.js
 */

class PredictiveSearchRecentSearches {
  constructor(container) {
    this.container = container;
    this.wrapper = container && container.querySelector('[data-recent-searches-wrapper]');
    this.storageKey = 'hr_recent_searches';
    this.limit = 4; // Samme som HR mobile.js
    this.init();
  }

  init() {
    if (!this.wrapper) return;
    this.buildRecentSearchField();
  }

  /**
   * Henter recent search terms fra localStorage
   * Safe localStorage access with error handling
   */
  getRecentSearchTerms() {
    try {
      if (typeof Storage === 'undefined') {
        return [];
      }
      const recentSearches = localStorage.getItem(this.storageKey);
      if (recentSearches) {
        try {
          const parsed = JSON.parse(recentSearches);
          // Validate that it's an array
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          // Error parsing recent searches - log only in development
          if (typeof window !== 'undefined' && window.console && window.console.error) {
            console.error('Error parsing recent searches:', e);
          }
          // Clear corrupted data
          try {
            localStorage.removeItem(this.storageKey);
          } catch (clearError) {
            // Ignore clear errors
          }
          return [];
        }
      } else {
        // Initialize with empty array
        try {
          localStorage.setItem(this.storageKey, JSON.stringify([]));
        } catch (setError) {
          // localStorage might be full or disabled
          if (typeof window !== 'undefined' && window.console && window.console.warn) {
            console.warn('Could not initialize recent searches storage:', setError);
          }
        }
        return [];
      }
    } catch (e) {
      // localStorage might be disabled or unavailable
      if (typeof window !== 'undefined' && window.console && window.console.warn) {
        console.warn('localStorage not available:', e);
      }
      return [];
    }
  }

  /**
   * Opdaterer localStorage med recent search terms
   * Safe localStorage access with error handling
   */
  updateRecentSearchStorage(searchTermsList) {
    try {
      if (typeof Storage === 'undefined') {
        return;
      }
      // Validate input
      if (!Array.isArray(searchTermsList)) {
        if (typeof window !== 'undefined' && window.console && window.console.warn) {
          console.warn('updateRecentSearchStorage: Expected array, got', typeof searchTermsList);
        }
        return;
      }
      const uniqueRecentSearchTerms = Array.from(new Set(searchTermsList));
      localStorage.setItem(this.storageKey, JSON.stringify(uniqueRecentSearchTerms));
    } catch (e) {
      // localStorage might be full, disabled, or in private mode
      if (typeof window !== 'undefined' && window.console && window.console.warn) {
        console.warn('Could not update recent searches storage:', e);
      }
    }
  }

  /**
   * Gemmer en søgeterm til recent searches
   */
  saveRecentSearchTerm(searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) return;
    
    const recentSearchTerms = this.getRecentSearchTerms();
    const trimmedTerm = searchTerm.trim();
    
    // Fjern eksisterende forekomst hvis den findes
    const filtered = recentSearchTerms.filter(term => term !== trimmedTerm);
    
    // Tilføj til starten
    filtered.unshift(trimmedTerm);
    
    // Begræns til limit
    if (filtered.length > this.limit) {
      filtered.pop();
    }
    
    this.updateRecentSearchStorage(filtered);
  }

  /**
   * Bygger recent search UI
   */
  buildRecentSearchField() {
    const recentSearchList = this.getRecentSearchTerms();
    
    if (!this.wrapper) return;
    
    if (!recentSearchList.length) {
      this.container?.classList.add('hidden');
      return;
    } else {
      this.container?.classList.remove('hidden');
    }
    
    this.wrapper.innerHTML = '';
    
    for (const searchTitle of recentSearchList) {
      if (!searchTitle) continue;
      
      const recentSearchContainer = document.createElement('div');
      recentSearchContainer.classList.add('predictive-search-recent-searches__item');
      // Escape searchTitle for data attribute to prevent XSS
      const escapedSearchTitle = searchTitle.replace(/"/g, '&quot;');
      recentSearchContainer.setAttribute('data-recent-search', escapedSearchTitle);
      
      // Clock icon - 12x12px to match HR design
      const clockIcon = document.createElement('div');
      clockIcon.classList.add('predictive-search-recent-searches__icon', 'predictive-search-recent-searches__icon--clock');
      clockIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/>
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/>
        </svg>
      `;
      
      // Search term text - use textContent to prevent XSS
      const pElement = document.createElement('p');
      pElement.textContent = searchTitle; // Safe: textContent automatically escapes HTML
      pElement.classList.add('predictive-search-recent-searches__term');
      
      // Remove icon - 17x17px to match HR design
      const removeIcon = document.createElement('button');
      removeIcon.classList.add('predictive-search-recent-searches__remove');
      removeIcon.setAttribute('aria-label', 'Fjern søgning');
      removeIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="currentColor" viewBox="0 0 16 16">
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
        </svg>
      `;
      
      recentSearchContainer.appendChild(clockIcon);
      recentSearchContainer.appendChild(pElement);
      recentSearchContainer.appendChild(removeIcon);
      
      this.wrapper.appendChild(recentSearchContainer);
    }
    
    // Event listeners
    this.wrapper.addEventListener('click', (e) => {
      const recentSearchItem = e.target.closest('[data-recent-search]');
      if (!recentSearchItem) return;
      
      const recentSearchTerm = recentSearchItem.dataset.recentSearch;
      
      // Remove if user clicks on remove button
      if (e.target.closest('.predictive-search-recent-searches__remove')) {
        e.stopPropagation();
        const recentSearchTerms = this.getRecentSearchTerms();
        const recentSearchTermsUpdated = recentSearchTerms.filter(term => term !== recentSearchTerm);
        this.updateRecentSearchStorage(recentSearchTermsUpdated);
        this.buildRecentSearchField();
        return;
      }
      
      // Search again with the recent search word if click on the term or clock icon
      if (e.target.closest('.predictive-search-recent-searches__term') || 
          e.target.closest('.predictive-search-recent-searches__icon--clock')) {
        const input = document.querySelector('.js-search-input');
        if (input) {
          input.value = recentSearchTerm;
          input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
          input.focus();
        }
      }
    });
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PredictiveSearchRecentSearches;
}

// Make available globally (only if not already defined to prevent re-declaration errors)
if (typeof window !== 'undefined' && !window.PredictiveSearchRecentSearches) {
  window.PredictiveSearchRecentSearches = PredictiveSearchRecentSearches;
}

